import * as React from 'react'
import { IBaseProduct, IProductSearchFilter, INestedProduct } from '@getgreenline/homi-shared'
import { SimilarProductList } from '../../../containers/Dashboard/DashboardProducts/AddProduct/Components/SimiliarProductList'
import { Input } from 'antd'
import debounce from 'lodash.debounce'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { GrRegex } from '../../../utilities/helpers'
import { productApi } from '@getgreenline/products'

interface Props {
  value: string
  size?: 'small' | 'default' | 'large'
  wrapperClassName?: string
  className?: string
  productId: string | null
  disabled?: boolean
  placeholder?: string
  addonAfter?: JSX.Element
  filterProducts?: (searchQuery: string, products: IBaseProduct[]) => IBaseProduct[]
  onChange: (value: string) => void
  blockSaving?: (blockSaving: boolean, errorMessage?: string) => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  productResults: IBaseProduct[]
  isDropdownMenuOpen: boolean
  isInitialLoading: boolean
  isLoading: boolean
  blur: boolean
}

@inject('currentCompanyStore')
@observer
export class AsyncSearchInputWrapper extends React.Component<
  React.PropsWithChildren<Props> & InjectedProps,
  State
> {
  MIN_QUERY_LENGTH = 2

  DEBOUNCE_TIME = 300

  companyId = this.props.currentCompanyStore!.company!.id

  timeout: NodeJS.Timer | undefined

  constructor(props: Props) {
    super(props)

    this.state = {
      productResults: [],
      isDropdownMenuOpen: false,
      isInitialLoading: true,
      isLoading: false,
      blur: false,
    }
  }

  onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onChange(e.target.value)
    this.debounceFetchProducts(e.target.value)
  }

  onBlur = () => {
    // Reference
    // https://medium.com/@jessebeach/dealing-with-focus-and-blur-in-a-composite-widget-in-react-90d3c3b49a9b
    this.timeout = setTimeout(() => {
      if (this.state.isDropdownMenuOpen) {
        this.setState({ isDropdownMenuOpen: false })
      }
      this.setState({ blur: true })
    }, 0)
  }

  onFocus = async () => {
    this.setState({
      blur: false,
    })

    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    // We don't want to show the "similar products dropdown at the initial loading on edit page"
    // Show the dropdown only when the name input is on focus
    await this.setState({
      isInitialLoading: this.state.isInitialLoading && false,
    })

    if (!this.state.isDropdownMenuOpen) {
      await this.setState({
        isDropdownMenuOpen: this.state.productResults.length > 0,
      })
    }
  }

  compileUniqueProducts = (nestedProducts: INestedProduct[]): IBaseProduct[] => {
    const mappedProducts = new Map<string, IBaseProduct>()

    for (const product of nestedProducts) {
      const { childProducts, ...productWithoutChildren } = product

      if (!mappedProducts.get(product.id)) {
        mappedProducts.set(product.id, productWithoutChildren)
      }

      childProducts.forEach((childProduct) => {
        if (!mappedProducts.get(childProduct.id)) {
          mappedProducts.set(childProduct.id, childProduct)
        }
      })
    }

    return [...mappedProducts.values()]
  }

  fetchProducts = async (searchQuery: string) => {
    const shouldFetchStart =
      searchQuery.length >= this.MIN_QUERY_LENGTH &&
      this.getUniqueUnicodeArray(searchQuery) === null

    if (!shouldFetchStart) {
      return this.setState({
        isDropdownMenuOpen: false,
        isLoading: false,
        productResults: [],
      })
    }

    await this.setState({
      isLoading: true,
      isDropdownMenuOpen: true,
    })

    const baseSearchFilter: IProductSearchFilter = {
      searchQuery,
      limit: 5,
    }

    const [deletedProductResponse, activeProductResponse] = await Promise.all([
      await productApi.getProducts(this.companyId, {
        ...baseSearchFilter,
        isDeleted: true,
      }),
      await productApi.getProducts(this.companyId, {
        ...baseSearchFilter,
        isActive: true,
      }),
    ])

    let flattenedProducts = this.compileUniqueProducts([
      ...deletedProductResponse.products,
      ...activeProductResponse.products,
    ])

    if (this.props.filterProducts) {
      flattenedProducts = this.props.filterProducts(searchQuery, flattenedProducts)
    }

    this.setState({
      isLoading: false,
      isDropdownMenuOpen: flattenedProducts.length > 0,
      productResults: flattenedProducts,
    })
  }

  debounceFetchProducts = debounce(this.fetchProducts, this.DEBOUNCE_TIME)

  getUniqueUnicodeArray = (value: string): unknown[] | null => {
    const uniqueArray = value.toString().match(GrRegex.unicodeRegex)

    return uniqueArray === null ? null : [...new Set(uniqueArray)]
  }

  render() {
    const { value, size, wrapperClassName, className, disabled, placeholder, addonAfter } =
      this.props
    const { productResults, isDropdownMenuOpen, isInitialLoading, isLoading, blur } = this.state

    return (
      <div
        className={wrapperClassName}
        style={{ position: 'relative' }}
        onBlur={this.onBlur}
        onFocus={this.onFocus}
      >
        <Input
          size={size || 'large'}
          value={value}
          className={className}
          disabled={disabled}
          placeholder={placeholder}
          addonAfter={addonAfter}
          onChange={this.onChange}
        />

        {!blur && isDropdownMenuOpen && !isInitialLoading && (
          <SimilarProductList searchQuery={value} products={productResults} isLoading={isLoading} />
        )}

        {this.props.children}
      </div>
    )
  }
}
